// Template module loading (§5.2–§5.3). Classic is eager (`manifest.module`),
// so reading it never suspends. Every other template loads through
// `manifest.load()` once; `readTemplateModule` suspends (throws the promise)
// until it resolves, which shows the app-level Suspense fallback on a cold
// first paint. Mid-session switches preload first and swap inside
// startTransition, so the old template stays on screen (no loader flash).

import { matchPath } from 'react-router-dom'
import type { SiteTemplate, SiteTemplateManifest } from './types'
import { SITE_ROUTES } from '../routes'
import { loadController } from '../pages/controllers'
import type { SiteBrand } from './brand'
import type { ThemeColors } from '@/services/api/types'

// The brand pipeline (OKLCH solver) is loaded with each lazy template rather
// than bundled into the entry: classic (the only eager template) has an
// empty brand() and never needs it.
type DeriveSiteBrand = (theme: ThemeColors) => SiteBrand
let deriveSiteBrand: DeriveSiteBrand | null = null
const loadBrand = () => import('./brand').then((m) => { deriveSiteBrand = m.deriveSiteBrand })

/** The SiteBrand builder once any lazy template has loaded; null for eager-only sessions. */
export function brandDeriver(manifest: SiteTemplateManifest): DeriveSiteBrand | null {
  return manifest.module ? null : deriveSiteBrand
}

/**
 * #627: fetch this URL's page controller (core, independent of the template's chunks)
 * alongside the template chunk, so the page's data can start as soon as its view lands.
 * Errors are left to the real load (PageRoute's lazy) to surface. The template's own
 * page chunks aren't preloaded from here: importing them from the entry splits the
 * template chunk into several shared ones, which costs more than it saves.
 */
function preloadCurrentController() {
  if (typeof window === 'undefined') return
  const route = SITE_ROUTES.find((r) => matchPath(r.path, window.location.pathname))
  if (!route) return
  void loadController(route.page).catch(() => {})
}

type Entry =
  | { status: 'pending'; promise: Promise<SiteTemplate> }
  | { status: 'done'; value: SiteTemplate }
  | { status: 'error'; error: unknown; promise: Promise<SiteTemplate> }

const cache = new Map<string, Entry>()

export function loadTemplateModule(manifest: SiteTemplateManifest): Promise<SiteTemplate> {
  if (manifest.module) return Promise.resolve(manifest.module)
  const hit = cache.get(manifest.id)
  if (hit) return hit.status === 'done' ? Promise.resolve(hit.value) : hit.promise
  if (!manifest.load) return Promise.reject(new Error(`site template "${manifest.id}" has neither module nor load`))
  preloadCurrentController()
  const promise = Promise.all([manifest.load(), loadBrand()]).then(
    ([m]) => {
      cache.set(manifest.id, { status: 'done', value: m.default })
      return m.default
    },
    (error) => {
      cache.set(manifest.id, { status: 'error', error, promise })
      throw error
    },
  )
  cache.set(manifest.id, { status: 'pending', promise })
  return promise
}

/** The template module, or suspends until it has loaded. Throws a load error to the nearest error boundary. */
export function readTemplateModule(manifest: SiteTemplateManifest): SiteTemplate {
  if (manifest.module) return manifest.module
  const entry = cache.get(manifest.id)
  if (entry?.status === 'done') return entry.value
  if (entry?.status === 'error') {
    cache.delete(manifest.id) // allow a retry on the next render
    throw entry.error
  }
  throw entry?.promise ?? loadTemplateModule(manifest)
}

export function isTemplateModuleReady(manifest: SiteTemplateManifest): boolean {
  return Boolean(manifest.module) || cache.get(manifest.id)?.status === 'done'
}
